import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react-lite";
import { Flex, Text, Dropdown } from "@fluentui/react-northstar";
import Constants from "../../../constants/text-constants";
import CallIconAvatar from "../../atoms/CallIconAvatar";
import GreyCallIconAvatar from "../../atoms/GreyCallIconAvatar";
import { useMst } from "../../../hooks/useMst";

const LocationDropDown = observer((props) => {
  const noResultsMsg = "We couldn't find any matches";
  const { locations, currentLocationCode, phoneNumber, onChange } = props;
  const [renderedItems, setRenderedItems] = useState([]);
  const [noResultsItem, setNoResultsItem] = useState(noResultsMsg);
  const { permissionStore } = useMst();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchQueryText, setSearchQueryText] = useState(undefined);

  const hasChangRegNoPermission = permissionStore.havePermissions(
    "Registration",
    ["CngRegNo"]
  );

  useEffect(() => {
    const allDropdownItems = locations.map((location, id) => {
      return {
        header: { 
          content: location.description,
          key: `header_${id}`
        },
        key: location.code,
        content: {
          content: location.phoneNumber,
          key: `content_${id}`
        },
        image: {
          as: CallIconAvatar,
        },
        code: location.code,
        selected: location.code === currentLocationCode,
        active: location.code === currentLocationCode,
      };
    });
    setRenderedItems(allDropdownItems);

    let selectedItm = null;
    if (currentLocationCode) {
      selectedItm = allDropdownItems.find((item) => {
        return item.code === currentLocationCode;
      });
    } else if (phoneNumber) {
      selectedItm = { header: {content: phoneNumber} };
    }

    setSelectedItem(selectedItm);
  }, [locations, currentLocationCode, phoneNumber]);

  useEffect(() => {
    setSearchQueryText(selectedItem ? itemToString(selectedItem) : undefined);
  }, [selectedItem]);

  const itemToString = (item) => {
    let displayString = "";
    if (item) {
      displayString = `${item.header?.content}`;

      if (item.content) {
        displayString += ` (${item.content?.content})`;
      }
      return displayString;
    }
    return "";
  };

  const onSearchQueryChange = (e, args) => {
    setIsOpen(true);
    const { searchQuery } = args;
    setSearchQueryText(searchQuery);

    let noResultsItm = null;

    // if string is numeric, show clickable no results entry
    if (searchQuery !== "" && !isNaN(searchQuery) && hasChangRegNoPermission) {
      noResultsItm = {
        header: {content: searchQuery},
        image: {
          as: GreyCallIconAvatar,
        },
        className: "temporary-number-item",
        onClick: onItemSelected,
      };
      setNoResultsItem(noResultsItm);
    } else {
      setNoResultsItem(noResultsMsg);
    }
  };

  const searchFunc = (data, query) => {
    return data.filter((locationItem) => {
      let itemString = itemToString(locationItem);
      return itemString.toLowerCase().includes(query.toLowerCase());
    });
  };

  const onItemSelected = (e, item) => {
    if(item.value === null) {
      // Clear 'X' has been clicked as item.value is null
      setSearchQueryText();
      setIsOpen(true);
      return;
    }

    if (!item.value && !item.header) return;
    let newLocation = { code: null, phoneNumber: null };

    if (item.value) {
      // saved location use the item's code
      newLocation.code = item.value.code;
    } else if (item.header?.content) {
      // temporary number entered
      newLocation.phoneNumber = item.header?.content;
    }
    onChange(newLocation);
    setIsOpen(false);
  };

  const onOpenChange = (e, { open }) => {
    setIsOpen(open);
  };

  return (
    <Flex column className="location-dropdown-container">
      <Text data-testid="locationDropdownTitle" className="body-text">
        {Constants.LOCATION}
      </Text>
      <Dropdown
        data-testid="locationDropdown"
        className="location-dropdown"
        search={searchFunc}
        itemToString={itemToString}
        defaultSearchQuery={searchQueryText}
        searchQuery={searchQueryText}
        onChange={onItemSelected}
        onSearchQueryChange={onSearchQueryChange}
        items={renderedItems}
        placeholder="Select or enter a location"
        noResultsMessage={noResultsItem}
        fluid
        open={isOpen}
        onOpenChange={onOpenChange}
        clearable
        value={searchQueryText}
      />
    </Flex>
  );
});

LocationDropDown.propTypes = {
  locations: PropTypes.array,
  currentLocationCode: PropTypes.string,
  phoneNumber: PropTypes.string,
  onChange: PropTypes.func,
};

LocationDropDown.defaultProps = {
  locations: [],
};

export default LocationDropDown;
